import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const storedUser = authService.getUser();
      if (storedUser && authService.getToken()) {
        // Verify token is still valid
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        } else {
          authService.logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (userName, password) => {
    const result = await authService.login(userName, password);
    if (result.success) {
      setUser(result.user);
      return result;
    }
    return result;
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  // Permission lookup used to hide what a user may not do. The API enforces
  // the same rules — this only keeps the UI honest.
  const can = (module, action = 'View') => {
    if (!user) return false;
    if (user.isSuperAdmin || user.isOrgAdmin) return true;

    const row = (user.permissions || []).find((p) => p.module === module);
    if (!row) return false;
    return Boolean(row[`can${action}`]);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin: user?.isSuperAdmin === true,
    isOrgAdmin: user?.isOrgAdmin === true,
    isOwner: user?.isOwner === true,
    permissions: user?.permissions || [],
    can,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

